import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearch } from "@tanstack/react-router";
import { AutoLoadMore, DataTable, PageHeader, useToast } from "../../components/ui";
import { EmptyListMark, EmptySearchMark, EmptyState, ErrorState } from "../../components/States";
import { formatDateTime, formatRelative } from "../../lib/format";
import { channelTypeLabel, notificationsApi, notifyKeys, type DeliveryRow } from "./api";
import { NotificationsTabs } from "./components";

const STATUSES = ["", "pending", "sent", "failed", "dead", "rate_limited"] as const;
const STATUS_LABELS: Record<string, string> = { pending: "Pending", sent: "Sent", failed: "Failed", dead: "Gave up", rate_limited: "Rate limited" };

export function DeliveriesPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const search = useSearch({ strict: false }) as { channelId?: string; status?: string };
  const qc = useQueryClient();
  const toast = useToast();
  const [channelId, setChannelId] = useState(search.channelId ?? "");
  const [status, setStatus] = useState(search.status ?? "");
  const filters = { channelId: channelId || undefined, status: status || undefined };
  const deliveries = useInfiniteQuery({
    queryKey: notifyKeys.deliveries(orgId, filters),
    queryFn: ({ pageParam }) => notificationsApi.deliveries(orgId, { ...filters, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = deliveries.data?.pages.flatMap((page) => page.items) ?? [];
  const channels = useQuery({ queryKey: notifyKeys.channels(orgId), queryFn: () => notificationsApi.channels(orgId) });
  const replay = useMutation({
    mutationFn: (id: string) => notificationsApi.replayDelivery(orgId, id),
    onSuccess: () => {
      toast.push("info", "Delivery re-queued.");
      void qc.invalidateQueries({ queryKey: ["notify", "deliveries", orgId] });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const channelName = (id: string) => channels.data?.find((c) => c.id === id)?.name ?? id;

  return (
    <>
      <PageHeader title="Delivery log">
        <NotificationsTabs orgId={orgId} />
        <div className="deliveries-filters" role="search" aria-label="Delivery filters">
          <select className="input" value={channelId} onChange={(e) => setChannelId(e.target.value)} aria-label="Channel" autoComplete="off">
            <option value="">All channels</option>
            {channels.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" autoComplete="off">
            {STATUSES.map((s) => <option key={s} value={s}>{s ? STATUS_LABELS[s] : "Any status"}</option>)}
          </select>
        </div>
      </PageHeader>
      <div className="card">
        {deliveries.isError ? (
          <ErrorState error={deliveries.error} retry={() => void deliveries.refetch()} />
        ) : deliveries.isSuccess && rows.length === 0 ? (
          channelId || status ? (
            <EmptyState mark={<EmptySearchMark />} title="No deliveries match" action={<button className="btn" onClick={() => { setChannelId(""); setStatus(""); }}>Clear filters</button>} />
          ) : (
            <EmptyState mark={<EmptyListMark />} title="No deliveries yet" />
          )
        ) : (
        <div className="deliveries-table">
        <DataTable
          ariaLabel="Deliveries"
          rows={rows}
          loading={deliveries.isPending || deliveries.isFetchingNextPage}
          rowKey={(d) => d.id}
          columns={[
            { key: "at", header: "When", label: "When", render: (d) => <span title={formatDateTime(d.at)}>{formatRelative(d.at)}</span> },
            { key: "msg", header: "Message", render: (d) => d.title ? <span>{d.title}</span> : <span className="muted">—</span> },
            { key: "ch", header: "Channel", render: (d) => (<><strong>{channelName(d.channelId)}</strong><div className="muted small">{channelTypeLabel(d.channelType)} · {d.kind === "test" ? "Test" : (d.kind ?? "create")}</div></>) },
            { key: "status", header: "Status", render: (d) => <StatusBadge d={d} /> },
            { key: "code", header: "Response", label: "Response", render: (d) => (d.responseCode ? <span className="mono">{d.responseCode}</span> : <span className="muted">—</span>) },
            { key: "err", header: "Error", label: "Error", render: (d) => (d.error ? <span className="danger small" title={d.error}>{d.error.slice(0, 80)}</span> : <span className="muted">—</span>) },
            { key: "next", header: "Next attempt", label: "Next attempt", render: (d) => (d.nextAttemptAt ? formatRelative(d.nextAttemptAt) : <span className="muted">—</span>) },
            {
              key: "act",
              header: "",
              align: "right",
              render: (d) => (d.status === "failed" || d.status === "dead" || d.status === "rate_limited" ? <button className="btn ghost sm" onClick={() => replay.mutate(d.id)} disabled={replay.isPending}>Replay</button> : null),
            },
          ]}
        />
        </div>
        )}
        <AutoLoadMore hasMore={!!deliveries.hasNextPage} loading={deliveries.isFetchingNextPage} loadMore={() => void deliveries.fetchNextPage()} label="Loading older deliveries" />
      </div>
    </>
  );
}

function StatusBadge({ d }: { d: DeliveryRow }) {
  const cls = d.status === "sent" ? "ok" : d.status === "pending" ? "neutral" : d.status === "rate_limited" ? "warn" : "danger";
  return (
    <span className={`badge ${cls}`}>
      {d.status}
      {d.attempt > 1 ? ` ×${d.attempt}` : ""}
    </span>
  );
}
